import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';

import * as Luxon from 'luxon';
import VBox from 'components/common/VBox';
import Search from 'components/common/Search';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import './Select.css';
import WheelScroll from 'components/common/WheelScroll';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import focusElement from 'utils/focusElement';
import hotkey from 'utils/hotkey';
import useLayoutRect from 'hooks/common/useLayoutRect';
import useForceUpdate from 'hooks/common/useForceUpdate';

const none = () => null;

export default function Select(props) {
  const {
    id,
    reference, // Element
    placement,
    connection, // 'start-arrow' (default) | 'line' | 'end-arrow' | 'arrow' | 'none'
    width, // integer | 'auto'
    height, // integer | 'auto'
    veilable, // Boolean
    stickable, // Boolean
    movable, // Boolean
    closable, // Boolean
    escapable, // Boolean
    modal, // Boolean
    dim, // color (default=transparent)
    tags = [],
    type,
    title,
    description,
    contents,
    buttons = [],
    events = {},
    props: {
      defaultValue,
      renderer,
      size: ROW_LENGTH = 7,
      item_list = [],
    },
    close,
    ...rest
  } = props || {};

  const today = React.useMemo(() => Luxon.DateTime.now(), []);

  const ref = React.useRef();
  const searchRef = React.useRef();
  const rect = useLayoutRect(reference);
  const floatingRect = useRect(ref);

  React.useEffect(() => {
    const cancel = hotkey(['Escape']);
    const onKeyDown = (e) => {
      cancel(e, () => {
        e.preventDefault();
        e.stopPropagation();
        return events?.onClose();
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events?.onClose]);

  const style = React.useMemo(() => {
    const info = {};
    switch (placement) {
      case 'top-left': {
        break;
      }
      case 'top': {
        break;
      }
      case 'top-right': {
        break;
      }
      case 'bottom-left': {
        const top = rect.top + rect.height;
        info.top = top;
        info.left = rect.left;
        info.minWidth = rect.width;
        break;
      }
      case 'bottom': {
        break;
      }
      case 'bottom-right': {
        const top = rect.top + rect.height;
        const right = window.innerWidth - (rect.left + rect.width);
        info.top = top;
        info.right = right;
        break;
      }
      case 'left-top': {
        break;
      }
      case 'left': {
        break;
      }
      case 'left-bottom': {
        break;
      }
      case 'right-top': {
        break;
      }
      case 'right': {
        break;
      }
      case 'right-bottom': {
        break;
      }
      default: {
        break;
      }
    }
    none(floatingRect.top, floatingRect.left);
    return info;
  }, [placement, rect.height, rect.left, rect.top, rect.width, floatingRect.top, floatingRect.left]);

  const [rowIndex, setRowIndex] = React.useState(0);

  const forceUpdate = useForceUpdate();
  React.useEffect(() => {
    requestAnimationFrame(() => {
      const defaultValueIndex = Math.max(0, item_list.findIndex(item => item.value.toString() === (defaultValue || '').toString()));
      const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')];
      tabElements[defaultValueIndex]?.focus();

      // 포커스가 변하면서 기존 reference의 위치가 변할 경우를 위해 한번 더 렌더링
      requestAnimationFrame(() => {
        forceUpdate();
      });
    });
  }, [defaultValue, forceUpdate, item_list]);

  const max_visible_length = React.useMemo(() => Math.min(ROW_LENGTH, item_list.length), [ROW_LENGTH, item_list.length]);
  const list = React.useMemo(() => item_list.slice(rowIndex, rowIndex + max_visible_length), [item_list, rowIndex, max_visible_length]);

  React.useEffect(() => {
    const forwardA = hotkey(['ArrowDown']);
    const backwardA = hotkey(['ArrowUp']);
    const forwardT = hotkey(['Tab']);
    const backwardT = hotkey(['Shift+Tab']);
    const onKeyDown = (e) => {
      if (e.key === 'Enter') return;
      e.preventDefault();
      e.stopPropagation();
      const onForward = () => {
        focusElement(document.activeElement, 1, (e) => {
          if (document.activeElement.getAttribute('data-select-id') !== id) return true;
          if (e.getAttribute('data-select-id') !== id) {
            setRowIndex(r => 0);
            requestAnimationFrame(() => {
              const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')];
              tabElements[0]?.focus();
            });
            return false;
          }
          const dataIndex = +e.getAttribute('data-index');
          const rowIndex = +e.getAttribute('data-row-index');
          const halfRow = ROW_LENGTH * 0.5;
          if (rowIndex < halfRow) return true;
          if (item_list.length - halfRow < dataIndex) return true;
          setRowIndex(r => Math.max(0, Math.min(item_list.length - ROW_LENGTH, r + 1)));
          return true;
        });
      };
      const onBackward = () => {
        focusElement(document.activeElement, -1, (e) => {
          if (document.activeElement.getAttribute('data-select-id') !== id) return true;
          if (e.getAttribute('data-select-id') !== id) {
            setRowIndex(r => item_list.length - ROW_LENGTH);
            requestAnimationFrame(() => {
              const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')].reverse();
              tabElements[0]?.focus();
            });
            return false;
          }
          const dataIndex = +e.getAttribute('data-index');
          const rowIndex = +e.getAttribute('data-row-index');
          const halfRow = ROW_LENGTH * 0.5;
          if (halfRow - 1 < rowIndex) return true;
          if (dataIndex < halfRow - 1) return true;
          setRowIndex(r => Math.max(0, Math.min(item_list.length - ROW_LENGTH, r - 1)));
          return true;
        });
      }
      forwardA(e, onForward);
      forwardT(e, onForward);
      backwardA(e, onBackward);
      backwardT(e, onBackward);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [ROW_LENGTH, id, max_visible_length, item_list.length]);

  const onWheelUpward = React.useCallback((e) => {
    if (max_visible_length < ROW_LENGTH) return;
    setRowIndex(rowIndex => {
      return Math.min(item_list.length - max_visible_length, Math.max(0, rowIndex + 1));
    })
  }, [ROW_LENGTH, item_list.length, max_visible_length]);

  const onWheelDownward = React.useCallback((e) => {
    if (max_visible_length < ROW_LENGTH) return;
    setRowIndex(rowIndex => {
      return Math.min(item_list.length - max_visible_length, Math.max(0, rowIndex - 1));
    });
  }, [ROW_LENGTH, item_list.length, max_visible_length]);

  const onButtonKeyDown = React.useCallback((e) => {
    switch (e.key) {
      case 'Shift':
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Tab':
        return;
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        break;
      default:
        console.log(e.key);
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
        break;
    }
  }, []);
  return (
    <div
      {...ex('Dim')}
      {...{ onClick: events?.onOuterClick(ref) }}
    >
      <VBox
        {...ex(['Floating', 'SelectDropdown', ...tags], rest)}
        {...{
          ref,
          style,
        }}
      >
        <WheelScroll
          {...ex('Contents')}
          {...{
            onWheelUpward,
            onWheelDownward,
          }}
        >
          {list.map((item, index) => {
            return (
              <FocusableButton
                key={index}
                {...ex('SelectItem')}
                {...{
                  onClick: events?.onSubmit,
                  onKeyDown: onButtonKeyDown,
                  'data-select-id': id,
                  'data-index': item?.index,
                  'data-row-index': index,
                }}
              >
                {renderer(item)}
              </FocusableButton>
            )
          })}
        </WheelScroll>
      </VBox>
    </div>
  );
}
